/**
 * POST /admin/auth/refresh 실패 처리.
 *
 * 서버가 세션 만료·재사용·구토큰을 401로 세분화한다.
 * 프론트는 코드/상태(400·401)로 재시도 여부를 나누지 않고
 * refresh 실패면 즉시 재로그인한다. 사용자 문구는 서버 message를 쓴다.
 */

/** 서버 ErrorCode 이름. 신규 코드는 없다. */
export const AUTH_REFRESH_FAILURE_CODES = [
  'SESSION_EXPIRED',
  'REFRESH_TOKEN_REPLAYED',
  'INVALID_TOKEN',
  'INVALID_REFRESH_TOKEN',
] as const;

export type AuthRefreshFailureCode =
  (typeof AUTH_REFRESH_FAILURE_CODES)[number];

/** 하드 리다이렉트 뒤 로그인 화면에서 서버 문구를 보여 주기 위한 키 */
export const AUTH_SESSION_END_MESSAGE_KEY = 'auth-session-end-message';

type ApiErrorPayload = {
  code?: string;
  message?: string;
};

const asRecord = (value: unknown): Record<string, unknown> | null => {
  if (value != null && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
};

export const getApiErrorPayload = (error: unknown): ApiErrorPayload => {
  const err = error as { response?: { data?: unknown } };
  const data = asRecord(err?.response?.data);
  if (!data) return {};
  return {
    code: typeof data.code === 'string' ? data.code : undefined,
    message: typeof data.message === 'string' ? data.message : undefined,
  };
};

/** 서버가 내려준 사용자 문구. 없으면 null (프론트에서 새 문구를 만들지 않는다). */
export const getRefreshFailureMessage = (error: unknown): string | null => {
  const message = getApiErrorPayload(error).message?.trim();
  return message || null;
};

export const persistAuthSessionEndMessage = (message: string | null) => {
  if (typeof sessionStorage === 'undefined') return;
  if (message) {
    sessionStorage.setItem(AUTH_SESSION_END_MESSAGE_KEY, message);
    return;
  }
  sessionStorage.removeItem(AUTH_SESSION_END_MESSAGE_KEY);
};

export const consumeAuthSessionEndMessage = (): string | null => {
  if (typeof sessionStorage === 'undefined') return null;
  const message = sessionStorage.getItem(AUTH_SESSION_END_MESSAGE_KEY);
  sessionStorage.removeItem(AUTH_SESSION_END_MESSAGE_KEY);
  const trimmed = message?.trim();
  return trimmed || null;
};
